import { rotationFromDirection, View } from "@novorender/api";
import { glMatrix, quat, ReadonlyQuat, ReadonlyVec3, ReadonlyVec4, vec2, vec3, vec4 } from "gl-matrix";

import type { ObjectDataAccess } from "dataAccess";
import { getCameraDir } from "features/engine2D/utils";
import { CameraState, CameraType, ClippingPlane, type MainObjectPick } from "features/render";
import { getSurfaceNormal } from "features/render/planeCreationNormal";
import { pointToPlaneDistance } from "utils/math";
import { getObjectBoundsW, getSelectionBoundingSphere } from "utils/objectData";

export async function getTopDownParams({
    view,
    elevation,
    snapToNearestAxis,
    far,
}: {
    view: View;
    elevation: number | undefined;
    snapToNearestAxis?: boolean;
    far: number;
}): Promise<{
    position: vec3;
    rotation: quat;
    fov: number;
    far?: number;
}> {
    const { width, height } = view.canvas.getBoundingClientRect();
    const pick = await view.pick(Math.round(width / 2), Math.round(height / 2), {
        sampleDiscRadius: 200,
        async: true,
        searchType: "closestToPickCenter",
    });

    let position = vec3.clone(view.renderState.camera.position);
    let z = elevation ?? position[2];
    let fov: number;
    if (pick) {
        position = pick.position;
        // Idea behind the formula (based on top-down 3d->2d transition, but we apply it in any case):
        // There's a right triangle formed by camera, pick position in the center of the screen, and the top point of the screen.
        // Triangle is not exactly right because pick position might be slightly off the center.
        //      C
        //     /|
        //    / |
        //   /  |
        //  /   |
        // T____P
        // where C - camera, T - top of the screen, P - pick position
        // We know distance C_P and angle CT_CP (half pinhole FOV), so we can find T_P distance using tan(CT_CP)
        // Then T_P multiplied by 2 gives us screen height, which is ortho FOV.
        fov =
            Math.tan(glMatrix.toRadian(view.renderState.camera.fov / 2)) *
            vec3.dist(view.renderState.camera.position, position) *
            2;
        z = position[2] + vec3.dist(view.renderState.camera.position, position);
    } else {
        fov = z;
    }

    // Adjust Z and far to the scene's bounding box height to see the entire scene
    // Assuming there's no depth lower than -10000 and no height higher than 10000
    const zRange = vec2.fromValues(-10000, 10000);
    // NOTE: scene bbox is not reliable as of 2025-10-17 (can contain min/max floats)
    const bbox = view.renderState.scene?.config.aabb;
    if (bbox) {
        zRange[0] = Math.max(zRange[0], bbox.min[2]);
        zRange[1] = Math.min(zRange[1], bbox.max[2]);
    }
    const bs = view.renderState.scene?.config.boundingSphere;
    if (bs) {
        zRange[0] = Math.max(zRange[0], bs.center[2] - bs.radius);
        zRange[1] = Math.min(zRange[1], bs.center[2] + bs.radius);
    }

    const offset = 0.5; // offset around Z range
    position[2] = zRange[1] + offset;
    const minFar = Math.max(1000, far);
    const maxFar = 500_000;
    const newFar = Math.min(maxFar, Math.ceil(zRange[1] - zRange[0]) + offset * 2);

    return {
        position,
        rotation: getTopDownRotation(view, snapToNearestAxis),
        fov: Math.max(fov, 20),
        ...(newFar > minFar ? { far: newFar } : {}),
    };
}

export function getTopDownRotation(view: View, snapToNearestAxis = false) {
    return rotationFromDirection([0, 0, 1], snapToNearestAxis ? view.renderState.camera.rotation : undefined);
}

export function getSnapToPlaneParams({
    planeIdx,
    view,
    anchorPos,
    offset,
    inferOrthoFov,
}: {
    planeIdx: number;
    view: View;
    anchorPos?: ReadonlyVec3;
    offset?: number;
    /**
     * Set FOV based on the original distance from camera to the clipping plane (relevant for ortho camera)
     * to preserve visual distance from the plane.
     */
    inferOrthoFov?: boolean;
}): {
    position: vec3;
    rotation: quat;
    fov: number;
    far: number;
} {
    const p = view.renderState.clipping.planes[planeIdx].normalOffset;
    const dir = vec3.fromValues(p[0], p[1], p[2]);
    const rotation = rotationFromDirection(dir);
    let position: ReadonlyVec3;
    if (anchorPos) {
        position = vec3.clone(anchorPos);
        if (offset) {
            vec3.scaleAndAdd(position, position, dir, offset);
        }
    } else {
        const planePoint = vec3.scaleAndAdd(vec3.create(), vec3.create(), dir, p[3]);
        const v = vec3.sub(vec3.create(), view.renderState.camera.position, planePoint);
        const d = vec3.dot(v, dir);
        position = vec3.scaleAndAdd(vec3.create(), view.renderState.camera.position, dir, -d);
    }

    const fov = inferOrthoFov ? pointToPlaneDistance(view.renderState.camera.position, p) : view.renderState.camera.fov;

    return { position, rotation, fov, far: 0.001 };
}

export function isCrossSectionAndAlignedWithPlane(camera: CameraState, normalOffset: ReadonlyVec4) {
    if (camera.type !== CameraType.Orthographic) {
        return false;
    }
    const rotation = camera.goTo?.rotation;
    if (!rotation) {
        return false;
    }
    const far = camera.goTo?.far;
    if (far === undefined || Math.abs(far) > 1) {
        // If far is too big - it's not a cross section, just a regular ortho view
        // and our small (0 to 1) far clipping plane slider might be confusing.
        return false;
    }
    const cameraDir = getCameraDir(rotation);
    const planeNormal = vec3.fromValues(normalOffset[0], normalOffset[1], normalOffset[2]);
    return Math.abs(vec3.dot(planeNormal, cameraDir)) >= 0.99;
}

/** Minimum ortho frustum height (meters) to avoid a degenerate zero-height view. */
const ORTHO_MIN_FOV = 0.01;
/** Frustum height (meters) used only when nothing is visible on screen and there is no previous ortho fov. */
const ORTHO_FALLBACK_FOV = 20;
/** Extra meters added to each side of the near/far slab so edge geometry is never clipped. */
const ORTHO_DEPTH_MARGIN = 0.55;
/** Clamp range for the half-depth of the symmetric near/far slab (meters). */
const ORTHO_MIN_HALF_DEPTH = 100;
const ORTHO_MAX_HALF_DEPTH = 500_000;
/** Half-depth used when the scene bounding sphere is unavailable. */
const ORTHO_FALLBACK_HALF_DEPTH = 10_000;

/**
 * Multiplier on the fitted object's bounding-sphere diameter. The sphere already circumscribes the
 * object, so it over-frames non-spherical shapes; a value at/below 1 keeps the fit tight. Lower this
 * to zoom in further (values < 1 crop the sphere's corners, which the object rarely fills).
 */
export const ORTHO_FIT_MARGIN = 0.95;

/** Meters the reveal clip plane is pushed past the object's front face so the face itself isn't clipped. */
export const ORTHO_CLIP_FACE_MARGIN = 0.5;

/**
 * A picked face whose normal points along the view direction by more than this (cosine of the angle
 * from the view direction, i.e. within 60 degrees of it) is treated as the far side of the object.
 * Not 0: a pinhole camera sees faces at the edge of the screen that lean slightly away from its heading.
 */
const ORTHO_PICK_FAR_SIDE_COS = 0.5;

/**
 * The face the user clicked when selecting `mainObject` (`render.mainObjectPick`), if it is still usable
 * for the current view: the pick must belong to `mainObject`, have a usable normal, and that face must
 * still be on the side of the object the camera is looking at. Both the ortho fit rotation and the reveal
 * clip plane key off this, so they always agree on whether - and which way - to use the pick.
 *
 * The stored normal already faces the camera that clicked it (see useCanvasClickHandler), so one that now
 * points along the view direction means the camera has since moved round to the other side: the pick
 * describes the far side of the object, and aligning to or clipping at it would hide the object.
 *
 * This is judged by the camera's heading, not its position: an unaligned ortho fit recenters the camera
 * on the object, which can put it behind the plane of a side face it could see a moment ago without
 * changing which side of the object it is looking at.
 */
function getVisiblePickFace(
    mainObject: number,
    mainObjectPick: MainObjectPick | null | undefined,
    cameraRotation: ReadonlyQuat,
): { normal: vec3; position: vec3 } | undefined {
    if (!mainObjectPick || mainObjectPick.objectId !== mainObject) {
        return undefined;
    }
    const normal = getSurfaceNormal(mainObjectPick.normal);
    // `!(<=)` rather than `>` so a NaN dot product also counts as unusable.
    if (!normal || !(vec3.dot(normal, getCameraDir(cameraRotation)) <= ORTHO_PICK_FAR_SIDE_COS)) {
        return undefined;
    }
    return { normal, position: vec3.clone(mainObjectPick.position as vec3) };
}

/**
 * Rotation used when fitting the ortho camera to the selected object: square the camera up to the face
 * the user clicked when selecting it, i.e. put the view direction anti-parallel to that surface normal,
 * so the face is seen straight on rather than at whatever oblique angle the pinhole camera happened to
 * have. Roll comes from `rotationFromDirection` (world Z up, Y for a vertical normal), matching how the
 * cross-section / snap-to-plane views orient themselves.
 *
 * Falls back to the current camera rotation - preserving the viewing angle - when there is no usable
 * pick (see getVisiblePickFace): the object was selected from the model tree / search (the common case
 * for deep objects), the pick belongs to another object, or the camera is now behind the picked face.
 */
function getOrthoFitRotation(
    cameraRotation: quat,
    mainObject: number,
    mainObjectPick: MainObjectPick | null | undefined,
): { rotation: quat; faceAligned: boolean } {
    const face = getVisiblePickFace(mainObject, mainObjectPick, cameraRotation);
    if (!face) {
        return { rotation: quat.clone(cameraRotation), faceAligned: false };
    }
    // rotationFromDirection takes the direction from the target *towards* the camera - the face normal.
    return { rotation: rotationFromDirection(face.normal), faceAligned: true };
}

/**
 * Builds an ortho pose that frames the currently selected object (`render.mainObject`), squared up to
 * the face it was picked on when there is one.
 *
 * The object's world-space bounding sphere is resolved the same way the axis sphere navigation does
 * (via `getSelectionBoundingSphere`, honoring the `scene.up` flip), so behaviour matches its known-
 * good framing. The camera is squared up to the face the user clicked when selecting the object (see
 * getOrthoFitRotation); without such a pick the current rotation is kept, so the user's heading doesn't
 * change. Either way the camera is centered on the object *laterally* (perpendicular to the view). For
 * ortho the position ALONG the view direction doesn't affect the image (only the lateral offset and fov
 * do), so it's chosen to keep the pose sane when toggling back to pinhole: unchanged when the rotation
 * is, else standing off in front of the picked face by the distance the camera already was from the
 * object. The frustum height (`fov`) is sized to the sphere's diameter, honoring the viewport aspect so
 * the sphere fits horizontally too, plus a small margin. Using the sphere makes the fit rotation-
 * independent (no need to project box corners onto the camera axes). The near/far slab stays scene-sized
 * (see computeOrthoNearFar) so the frustum never clips geometry as the camera orbits; revealing a deep
 * object is handled separately by a world-fixed clip plane (see getOrthoFitClipPlane).
 *
 * Returns `undefined` when there is no selected object, no data access, or its bounds are unavailable /
 * degenerate, letting the caller fall back to the plain projection-only conversion.
 */
async function getOrthoFittingMainObject(
    view: View,
    cam: { position: ReadonlyVec3; rotation: quat; fov: number },
    dataAccess: ObjectDataAccess | undefined,
    up: ReadonlyVec3 | undefined,
    mainObject: number | undefined,
    mainObjectPick: MainObjectPick | null | undefined,
): Promise<{ position: vec3; rotation: quat; fov: number; near: number; far: number } | undefined> {
    if (mainObject === undefined || !dataAccess) {
        return undefined;
    }

    let sphere: { center: ReadonlyVec3; radius: number } | undefined;
    try {
        sphere = await getSelectionBoundingSphere({
            ids: [mainObject],
            dataAccess,
            // Bounds are in CAD space; flip to world when the scene isn't already Z-up (matches axis sphere).
            flipCoords: !vec3.equals(up ?? [0, 1, 0], [0, 0, 1]),
        });
    } catch {
        return undefined;
    }
    // Bounds can occasionally contain non-finite floats or a zero radius; bail to the fallback if so.
    if (!sphere || !sphere.radius || !Number.isFinite(sphere.radius) || ![...sphere.center].every(Number.isFinite)) {
        return undefined;
    }

    const { rotation, faceAligned } = getOrthoFitRotation(cam.rotation, mainObject, mainObjectPick);
    const sphereCenter = sphere.center as vec3;

    const { width, height } = view.canvas.getBoundingClientRect();
    const aspect = height > 0 ? width / height : 1;
    // ortho fov is the vertical frustum height; horizontal coverage is fov * aspect. Grow fov so the
    // sphere's diameter fits whichever viewport dimension is narrower.
    const diameter = sphere.radius * 2;
    const fov = Math.max(diameter, aspect > 0 ? diameter / aspect : diameter) * ORTHO_FIT_MARGIN;

    // Move the camera to the sphere center's lateral position (centering the object on screen) but keep
    // it a comparable distance away along the view direction, so switching back to pinhole doesn't leave
    // the camera sitting on (or behind) the object.
    const forward = getCameraDir(rotation);
    const depthOffset = faceAligned
        ? // The rotation changed, so the old along-view component says nothing about where the camera
          // should sit on the new axis - and can even land it behind the face we just turned to look at.
          // Back off along the face normal by the distance the camera already was from the object, at
          // least clear of its bounding sphere (the camera can start inside a large selected object).
          Math.max(vec3.distance(cam.position as vec3, sphereCenter), sphere.radius)
        : // Rotation unchanged: subtracting the along-view component of (center - camera) from the center
          // leaves the lateral part of the center and the along-view part of the original position.
          vec3.dot(forward, vec3.subtract(vec3.create(), sphereCenter, cam.position as vec3));
    const position = vec3.scaleAndAdd(vec3.create(), sphereCenter, forward, -depthOffset);

    const { near, far } = computeOrthoNearFar(position, view);

    return { position, rotation, fov: Math.max(fov, ORTHO_MIN_FOV), near, far };
}

/**
 * Builds a world-fixed clip plane that reveals the selected object when switching to the orthographic
 * camera. Because ortho has infinite depth of field, an object deep inside the model stays occluded by
 * whatever is in front of it; a plane at the object's front face hides that occluding geometry. Unlike
 * the camera near plane, a clip plane is anchored in world space, so it keeps revealing the object
 * without slicing into it as the camera orbits.
 *
 * The plane normal comes from the face the user clicked when selecting the object (`render.mainObjectPick`,
 * captured at selection time so it isn't clobbered by later hovers). When there is no usable pick (see
 * getVisiblePickFace) - e.g. deep objects picked from the model tree / search, or the camera has since
 * moved behind the picked face - it falls back to the current view direction, i.e. a plane facing the
 * camera. Either way the plane is positioned at the object's frontmost extent along that normal (via `getObjectBoundsW`) plus a small margin, not at the
 * pick point, so the whole object is kept rather than sliced. In the default union clipping mode the
 * hidden half-space is `dot(n, x) > w`, so the normal is oriented towards the camera.
 *
 * Returns `undefined` (caller adds no plane) when there is no selected object, or when neither the object
 * bounds nor a captured pick give a place to put the plane.
 */
export async function getOrthoFitClipPlane({
    view,
    dataAccess,
    mainObject,
    mainObjectPick,
}: {
    view: View;
    dataAccess: ObjectDataAccess | undefined;
    /** `render.mainObject` - the object to reveal. */
    mainObject: number | undefined;
    /** `render.mainObjectPick` - the face clicked when selecting it, if any. */
    mainObjectPick?: MainObjectPick | null;
}): Promise<Omit<ClippingPlane, "color" | "outline"> | undefined> {
    if (mainObject === undefined) {
        return undefined;
    }

    // Prefer the face the user clicked to select this object; fall back to the view direction otherwise.
    // Either way the normal faces the camera, so the hidden half-space is the near (occluding) side.
    const cameraRotation = view.renderState.camera.rotation;
    const face = getVisiblePickFace(mainObject, mainObjectPick, cameraRotation);
    const anchorPos = face?.position;
    const normal = face?.normal ?? vec3.negate(vec3.create(), getCameraDir(cameraRotation));

    // Position the plane ORTHO_CLIP_FACE_MARGIN in front of the object's front face along the normal.
    // With a real clicked point, anchor exactly to it so the gap is just the margin. Otherwise (view-
    // direction fallback) use the object's AABB support along the normal - note this can sit noticeably
    // farther out than the visible surface, since the box is a loose fit for diagonal/elongated parts.
    let w: number;
    if (anchorPos) {
        w = vec3.dot(normal, anchorPos);
    } else {
        let boundsMax: number | undefined;
        try {
            boundsMax = dataAccess ? (await getObjectBoundsW(dataAccess, mainObject, normal))?.max : undefined;
        } catch {
            // Handled by the undefined check below.
        }
        if (boundsMax === undefined) {
            return undefined;
        }
        w = boundsMax;
    }
    w += ORTHO_CLIP_FACE_MARGIN;

    return {
        normalOffset: vec4.fromValues(normal[0], normal[1], normal[2], w),
        baseW: w,
        ...(anchorPos ? { anchorPos } : {}),
        showPlane: false,
        temporary: true,
    };
}

/**
 * Computes the orthographic near/far clipping slab sized to the scene bounding sphere
 * from `position`. The slab is symmetric and large enough to contain the full scene
 * regardless of camera orientation, preventing clipping when orbiting or panning.
 *
 * Use this whenever the ortho camera moves to a new position and explicit near/far
 * values are not already supplied by the caller (e.g. axis sphere navigation).
 */
export function computeOrthoNearFar(position: ReadonlyVec3, view: View): { near: number; far: number } {
    const boundingSphere = view.renderState.scene?.config.boundingSphere;
    const sceneHalfDepth = boundingSphere
        ? vec3.distance(position as vec3, boundingSphere.center as vec3) + boundingSphere.radius
        : ORTHO_FALLBACK_HALF_DEPTH;
    const halfDepth = Math.min(
        Math.max(sceneHalfDepth + ORTHO_DEPTH_MARGIN, ORTHO_MIN_HALF_DEPTH),
        ORTHO_MAX_HALF_DEPTH,
    );
    return { near: -halfDepth, far: halfDepth };
}

/**
 * Builds an orthographic camera pose from the current (pinhole) camera, changing only the projection.
 *
 * - When an object is selected (`render.mainObject`), the camera is recentered on it and the fov is
 *   sized to frame it tightly. If the object was selected by clicking a surface (`render.mainObjectPick`)
 *   the view direction is also aligned with that surface's normal so the face is seen straight on;
 *   otherwise the current viewing angle is preserved. See getOrthoFittingMainObject.
 * - Otherwise position and rotation are retained as-is (the switch must not reframe the view).
 * - `fov` is the ortho frustum height in meters, converted from the pinhole angular fov at the focus
 *   distance so the apparent scale matches at the content the user is looking at. The focus distance is
 *   resolved through a fallback chain so it stays anchored to *visible* content rather than the whole
 *   (potentially huge, sparse) scene:
 *     1. a small center-screen pick (the precise focal point),
 *     2. a viewport-wide pick so off-center content in sparse scenes is still found,
 *     3. the previous ortho fov, then a small constant, when the screen is genuinely empty.
 * - `near`/`far` form a symmetric depth slab sized to the scene bounding sphere around the camera. In
 *   ortho the near/far planes are perpendicular to the view direction, so a large surface viewed at a
 *   grazing angle spans a huge depth range; a thin slab (or a near plane kept close behind the camera)
 *   clips it and zooming only reveals more of the clipped band. Sizing both planes to the scene keeps
 *   it inside the frustum at any orientation or zoom. Ortho depth precision is linear so a wide range is
 *   safe, and lateral frustum culling still skips off-screen geometry.
 */
export async function getOrthoFromPinhole(
    view: View,
    options?: {
        /** Data access used to resolve the selected object's bounds (from explorer globals). */
        dataAccess?: ObjectDataAccess;
        /** Scene up vector (`scene.up`), used to flip CAD bounds into world space. */
        up?: ReadonlyVec3;
        /** `render.mainObject` - when set (with a `dataAccess`), the pose is fitted to frame it. */
        mainObject?: number;
        /** `render.mainObjectPick` - the face clicked when selecting it; aligns the view with its normal. */
        mainObjectPick?: MainObjectPick | null;
        camera?: {
            position: ReadonlyVec3;
            rotation: quat;
            fov: number;
        };
    },
): Promise<{
    position: vec3;
    rotation: quat;
    fov: number;
    near: number;
    far: number;
}> {
    const cam = options?.camera ?? view.renderState.camera;
    const fitted = await getOrthoFittingMainObject(
        view,
        cam,
        options?.dataAccess,
        options?.up,
        options?.mainObject,
        options?.mainObjectPick,
    );
    if (fitted) {
        return fitted;
    }

    const position = vec3.clone(cam.position as vec3);
    const rotation = quat.clone(cam.rotation);

    const { width, height } = view.canvas.getBoundingClientRect();
    const centerX = Math.round(width / 2);
    const centerY = Math.round(height / 2);

    // 1) Off-center / sparse scenes: widen the disc to the whole viewport so visible content away from
    //    the center is still found. "closestToPickCenter" keeps the hit nearest the middle. If the scene is focalized, it's likely that the focus pick will be found fast.
    const viewportRadius = Math.ceil((Math.max(width, height) * (window.devicePixelRatio || 1)) / 2);
    const focusPick = await view.pick(centerX, centerY, {
        sampleDiscRadius: viewportRadius,
        async: true,
        searchType: "closestToPickCenter",
    });

    // Convert the pinhole angular fov to an ortho frustum height at the focus distance. `depth` is the
    // perpendicular distance from the view plane, which is exactly what the conversion needs.
    let fov: number;
    if (focusPick) {
        const focusDistance = Math.max(focusPick.depth, 1);
        fov = 2 * Math.tan(glMatrix.toRadian(cam.fov / 2)) * focusDistance;
    } else {
        // 2) Genuinely empty screen: keep the previous ortho zoom if any, else a small constant.
        //    Never anchor to the whole scene bounds here - that is what made huge sparse scenes zoom out.
        const previousFov = view.controllers.ortho.fov;
        fov = Number.isFinite(previousFov) && previousFov > 0 ? previousFov : ORTHO_FALLBACK_FOV;
    }
    fov = Math.max(fov, ORTHO_MIN_FOV);

    // Symmetric slab sized to the scene from this camera position.
    const { near, far } = computeOrthoNearFar(position, view);

    return { position, rotation, fov, near, far };
}

export async function getBestAxisAlignedView(
    view: View,
    camera?: {
        position: vec3;
        rotation: quat;
        fov: number;
    },
): Promise<{
    position: vec3;
    rotation: quat;
    fov: number;
}> {
    const cam = camera ?? view.renderState.camera;
    // Get current camera forward direction
    const forward = vec3.create();
    vec3.transformQuat(forward, vec3.fromValues(0, 0, -1), cam.rotation);

    // Define standard orthographic view directions
    const axisAlignedViews = [
        {
            name: "bottom",
            direction: vec3.fromValues(0, 0, -1),
            rotation: rotationFromDirection(vec3.fromValues(0, 0, -1)),
        },
        {
            name: "top",
            direction: vec3.fromValues(0, 0, 1),
            rotation: rotationFromDirection(vec3.fromValues(0, 0, 1)),
        },
        {
            name: "back",
            direction: vec3.fromValues(0, 1, 0),
            rotation: rotationFromDirection(vec3.fromValues(0, 1, 0)),
        },
        {
            name: "front",
            direction: vec3.fromValues(0, -1, 0),
            rotation: rotationFromDirection(vec3.fromValues(0, -1, 0)),
        },
        {
            name: "left",
            direction: vec3.fromValues(1, 0, 0),
            rotation: rotationFromDirection(vec3.fromValues(1, 0, 0)),
        },
        {
            name: "right",
            direction: vec3.fromValues(-1, 0, 0),
            rotation: rotationFromDirection(vec3.fromValues(-1, 0, 0)),
        },
    ];

    // Find the orthographic view that shows what we're currently looking at
    // Select the view with the most opposite direction (lowest dot product)
    let bestMatch = axisAlignedViews[0];
    let lowestDotProduct = vec3.dot(forward, bestMatch.direction);

    for (const axisView of axisAlignedViews) {
        const dotProduct = vec3.dot(forward, axisView.direction);
        if (dotProduct < lowestDotProduct) {
            lowestDotProduct = dotProduct;
            bestMatch = axisView;
        }
    }
    const { width, height } = view.canvas.getBoundingClientRect();
    const pick = await view.pick(Math.round(width / 2), Math.round(height / 2), {
        sampleDiscRadius: 200,
        async: true,
        searchType: "closestToPickCenter",
    });

    if (pick) {
        // If we have a pick, we want to center the orthographic view on it.
        // We can calculate the camera position by moving from the pick position in the opposite direction of the view,
        // at a distance equal to the current camera distance to the pick point.
        const cameraToPick = vec3.distance(cam.position, pick.position);
        const position = vec3.scaleAndAdd(vec3.create(), pick.position, bestMatch.direction, cameraToPick);
        return {
            position,
            rotation: bestMatch.rotation,
            fov: cameraToPick,
        };
    }

    const sceneBoundingSphere = view.renderState.scene?.config.boundingSphere;

    // Calculate camera position for the selected orthographic view
    if (sceneBoundingSphere) {
        // calculate relative position to the center of the bounding sphere
        const cameraRadius = vec3.distance(cam.position, sceneBoundingSphere.center);
        const inside = cameraRadius < sceneBoundingSphere.radius;

        const cameraDirection = vec3.create();
        vec3.transformQuat(cameraDirection, vec3.fromValues(0, 0, -1), bestMatch.rotation);

        const position = vec3.scaleAndAdd(vec3.create(), sceneBoundingSphere.center, cameraDirection, -cameraRadius);

        const fovmul = inside ? 0.25 : 0.5;
        // clamp ortho FOV (frustum height in this case) to avoid extreme values when the camera is very close or very far from the scene
        const fov = Math.min(Math.max(cameraRadius * fovmul, 2.0), 250.0);

        return {
            position: inside ? cam.position : position,
            rotation: bestMatch.rotation,
            fov,
        };
    } else {
        // Fallback to current position if no bounding sphere available
        return {
            position: cam.position,
            rotation: bestMatch.rotation,
            fov: 5,
        };
    }
}
